import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, Stack, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import { describeError, isTechnical, newErrorReference } from '@/errors/describeError';

/**
 * Global toast (dispatch OPEN_SNACK_TOAST). Errors stay until closed and show a
 * plain-language message; when the error is technical, or a screen passes
 * `details`, they also get a reference that is logged on the server
 * (T_Proc_Exception, name "PORTAL-UI <ref>") and a Details dialog the user can
 * copy or email to IT support.
 */

const SUPPORT_EMAIL = (import.meta.env.APP_SUPPORT_EMAIL as string | undefined) ?? '';

const ErrorAlert = ({
  isOpen,
  title,
  message,
  severity,
  details,
  action,
}: {
  isOpen: boolean;
  title: string;
  message: string;
  severity?: 'error' | 'success' | 'warning' | 'info';
  /** exact technical error, when the screen has it separately from `message` */
  details?: string;
  /** what the user was doing, e.g. "Save sales order" */
  action?: string;
}) => {
  const dispatch = useDispatch();
  const { loggedUserData } = useSelector((state: any) => state.loginReducer ?? {});
  const lowerTitle = String(title || '').toLowerCase();
  const lowerMsg = String(message || '').toLowerCase();

  const isError =
    severity === 'error' ||
    (severity !== 'success' &&
      (title === 'Error' ||
        lowerTitle.includes('error') ||
        lowerTitle.includes('fail') ||
        lowerTitle.includes('cannot') ||
        lowerTitle.includes('warning') ||
        lowerTitle.includes('invalid') ||
        lowerTitle.includes('not found') ||
        lowerTitle.includes('denied') ||
        lowerMsg.includes('not found') ||
        lowerMsg.includes('failed') ||
        lowerMsg.includes('invalid') ||
        lowerMsg.includes('error')));

  // technical text: the exact error is kept for IT, the user sees a translation
  const technical = details || (isTechnical(message) ? message : '');
  const described = useMemo(() => describeError(technical, technical ? null : message), [technical, message]);
  const supportCase = isError && described.forSupport;
  const shown = supportCase ? described.friendly : message;

  const [reference, setReference] = useState<string | null>(null);
  const [openedAt, setOpenedAt] = useState<string>('');
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);
  const logged = useRef<string | null>(null);

  // one reference (and one server log entry) per error shown
  useEffect(() => {
    if (!isOpen || !supportCase) {
      if (!isOpen) {
        setReference(null);
        setShowDetails(false);
        setCopied(false);
      }
      return;
    }
    const ref = newErrorReference();
    setReference(ref);
    setOpenedAt(new Date().toLocaleString());
    if (logged.current === ref) return;
    logged.current = ref;
    axios
      .post('support/logError', {
        reference: ref,
        screen: window.location.pathname,
        action: action || title,
        message: shown,
        detail: technical || message,
      })
      .catch(() => {
        // logging must never get in the user's way
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, supportCase, message, details]);

  const detailText = [
    `Reference: ${reference ?? '-'}`,
    `Time: ${openedAt}`,
    `User: ${loggedUserData?.user_code ?? '-'}`,
    `Company / Division: ${loggedUserData?.comp_code ?? '-'} / ${loggedUserData?.divn_code ?? '-'} (${loggedUserData?.divn_name ?? ''})`,
    `Screen: ${window.location.pathname}`,
    action || title ? `Action: ${action || title}` : null,
    `Message shown: ${shown}`,
    `Exact error: ${technical || message}`,
  ]
    .filter(Boolean)
    .join('\n');

  const close = () => dispatch({ type: 'CLOSE_SNACK_TOAST' });
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(detailText);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    `ERP portal error ${reference ?? ''} – ${action || title || 'error'}`
  )}&body=${encodeURIComponent(`${detailText}\n\nWhat I was doing:\n`)}`;

  return (
    <>
      <Snackbar
        open={isOpen}
        // errors stay until the user closes them; success messages fade
        autoHideDuration={isError ? null : 3500}
        onClose={(_, reason) => {
          if (reason === 'clickaway' && isError) return;
          close();
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          icon={isError ? <ErrorIcon sx={{ color: '#fff' }} /> : <CheckCircleIcon sx={{ color: '#fff' }} />}
          action={
            <Stack direction="row" spacing={0.5} alignItems="center">
              {supportCase && (
                <Button
                  size="small"
                  onClick={() => setShowDetails(true)}
                  sx={{ color: '#fff', borderColor: 'rgba(255,255,255,.6)', textTransform: 'none', fontWeight: 700 }}
                  variant="outlined"
                >
                  Details
                </Button>
              )}
              <Button onClick={close} color="inherit" size="small" sx={{ color: '#fff', minWidth: 32 }}>
                X
              </Button>
            </Stack>
          }
          sx={{
            backgroundColor: isError ? '#d32f2f' : '#2e7d32',
            width: '100%',
            maxWidth: 640,
            color: '#fff',
            alignItems: 'center',
          }}
        >
          {supportCase && (action || (title && title !== 'Error')) && (
            <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>{action || title}</Typography>
          )}
          <Typography sx={{ fontSize: '0.875rem' }}>{shown}</Typography>
          {supportCase && described.hint && (
            <Typography sx={{ fontSize: '0.78rem', opacity: 0.9 }}>{described.hint}</Typography>
          )}
          {supportCase && reference && (
            <Typography sx={{ fontSize: '0.72rem', opacity: 0.85, mt: 0.25 }}>Reference {reference}</Typography>
          )}
        </Alert>
      </Snackbar>

      <Dialog open={showDetails} onClose={() => setShowDetails(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Error details for IT support</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.9rem', mb: 0.5 }}>{shown}</Typography>
          {described.hint && (
            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mb: 1.5 }}>{described.hint}</Typography>
          )}
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              bgcolor: '#0f172a',
              color: '#e2e8f0',
              borderRadius: '8px',
              fontSize: '0.75rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: 320,
              overflow: 'auto',
            }}
          >
            {detailText}
          </Box>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 1 }}>
            This error is also recorded on the server under reference {reference}, so IT can find it from the number
            alone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<ContentCopyRoundedIcon />} onClick={copy}>
            {copied ? 'Copied' : 'Copy details'}
          </Button>
          <Button startIcon={<MailOutlineRoundedIcon />} href={mailto}>
            Email IT support
          </Button>
          <Button variant="contained" onClick={() => setShowDetails(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ErrorAlert;
